import { Dialog, Popover } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import { IAlert, IDevice, ISensor } from '../../../../interfaces';
import api from '../../../../services/api';
import socket from '../../../../services/io';
import Notification from './Notification';
import {
  Container,
  FooterActions,
  MarkAllButton,
  ModalActions,
  ModalContainer,
  ModalMessage,
  ModalMeta,
  ModalTimestamp,
  ModalTitle,
  PrimaryButton,
  SecondaryButton,
} from './styles';

interface INotificationsPopUpProps {
  anchor: Element | null;
  handleClose: () => void;
  setNotification: (num: number) => void;
  isOpen: boolean;
}

interface INotificationAlert {
  id: string;
  device: IDevice;
  sensor?: ISensor;
  read?: boolean;
  message?: string;
  timestamp: Date;
  type: 'sensor' | 'battery';
}

interface IBatteryAlert {
  id: string;
  device: IDevice;
  device_id: string;
  message: string;
  read: boolean;
  timestamp: Date;
}

const MAX_ALERTS_DISPLAY = 50;
const MAX_ALERTS_TO_FETCH = 50;
const alertDateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const NotificationsPopUp: React.FC<INotificationsPopUpProps> = ({ anchor, handleClose, isOpen, setNotification }) => {
  const [alerts, setAlerts] = useState<INotificationAlert[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<INotificationAlert | null>(null);
  const [isMarkingAsRead, setIsMarkingAsRead] = useState(false);
  const [isBulkMarking, setIsBulkMarking] = useState(false);
  const { addToast } = useToast();
  const { user } = useAuth();

  const getUserAlerts = useCallback(async () => {
      const { data: receivedSensorAlerts } = await api.get<IAlert[]>('/alerts/user', {
        params: {
          user_id: user.id,
          alert_number: MAX_ALERTS_TO_FETCH,
        },
      });    
      
      const sensorAlerts: INotificationAlert[] = receivedSensorAlerts.map(item => ({
      id: item.id,
      device: item.sensor.device,
      sensor: item.sensor,
      message: item.message,
      timestamp: new Date(item.timestamp),
      read: item.read,
      type: 'sensor',
    }));

    const { data: receivedBatteryAlerts } = await api.get<IBatteryAlert[]>('/battery/alerts/user', {
      params: {
        user_id: user.id,
        alert_number: MAX_ALERTS_TO_FETCH,
      },
    });
    const batteryAlerts: INotificationAlert[] = receivedBatteryAlerts.map(item => ({
      id: item.id,
      device: item.device,
      message: item.message,
      timestamp: new Date(item.timestamp),
      read: item.read,
      type: 'battery',
    }));

    const allAlerts: INotificationAlert[] = [...sensorAlerts, ...batteryAlerts].sort((a, b) => {
      return b.timestamp.getTime() - a.timestamp.getTime();
    });

    const unreadAlerts = allAlerts.filter(alert => !alert.read);

    let selectedAlerts = unreadAlerts.slice(0, MAX_ALERTS_DISPLAY);

    if (selectedAlerts.length < MAX_ALERTS_DISPLAY) {
      const additionalAlerts = allAlerts.filter(alert => !selectedAlerts.includes(alert));
      selectedAlerts = [...selectedAlerts, ...additionalAlerts.slice(0, MAX_ALERTS_DISPLAY - selectedAlerts.length)];
    }

    setAlerts(selectedAlerts);
  }, [user.id]);

  const onSocketMessage = (data: string) => {
    const alert = JSON.parse(data);

    getUserAlerts();

    addToast({
      type: 'info',
      title: 'Alerta',
      description: alert.timestamp + " - " + alert.message,
    });
  };

  useEffect(() => {
    socket.on(user.id, onSocketMessage);

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    getUserAlerts();
  }, []);

  const refreshNotifications = async () => {
    const filterOnlyNotReadAlerts = alerts.filter(alert => !alert.read);

    setNotification(filterOnlyNotReadAlerts.length);
  };

  useEffect(() => {
    refreshNotifications();
  }, [alerts, setNotification]);

  const handleSelectAlert = (alert: INotificationAlert) => {
    setSelectedAlert(alert);
  };

  const handleCloseModal = () => {
    setSelectedAlert(null);
  };

  const handleMarkAsRead = async () => {
    if (!selectedAlert) {
      return;
    }

    const alertId = selectedAlert.id;
    const alertType = selectedAlert.type;

    try {
      setIsMarkingAsRead(true);
      if (alertType === 'sensor') {
        await api.put('/alerts/read', {
          alerts_id: [alertId],
        });
      } else {
        await api.put('/battery/alerts/read', {
          alerts_id: [alertId],
        });
      }

      setAlerts(prevAlerts => prevAlerts.filter(alert => alert.id !== alertId));
      handleCloseModal();
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao marcar alerta',
        description: 'Não foi possível marcar o alerta como lido. Tente novamente.',
      });
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!alerts.length) {
      return;
    }

    const sensorIds = alerts.filter(alert => alert.type === 'sensor').map(alert => alert.id);
    const batteryIds = alerts.filter(alert => alert.type === 'battery').map(alert => alert.id);

    try {
      setIsBulkMarking(true);

      const promises: Array<Promise<void>> = [];

      if (sensorIds.length) {
        promises.push(api.put('/alerts/read', { alerts_id: sensorIds }).then(() => undefined));
      }

      if (batteryIds.length) {
        promises.push(api.put('/battery/alerts/read', { alerts_id: batteryIds }).then(() => undefined));
      }

      await Promise.all(promises);

      setSelectedAlert(null);
      setAlerts([]);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao marcar alertas',
        description: 'Não foi possível marcar todas as notificações como lidas. Tente novamente.',
      });
    } finally {
      setIsBulkMarking(false);
    }
  };
  return (
    <>
      <Popover
        open={isOpen}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Container>
          {alerts.length > 0 && (
            <FooterActions>
              <MarkAllButton type="button" onClick={handleMarkAllAsRead} disabled={isBulkMarking}>
                Marcar todas lidas
              </MarkAllButton>
            </FooterActions>
          )}
          {alerts.length > 0 ? (
            alerts.map(alert => (
              <Notification
                onSelectNotification={() => handleSelectAlert(alert)}
                key={alert.id}
                device_name={alert.device.name}
                sensor_name={alert.sensor?.name}
                read={alert.read}
                type={alert.type}
              />
            ))
          ) : (
            <strong> Nenhum alerta encontrado.</strong>
          )}
        </Container>
      </Popover>

      <Dialog
        open={Boolean(selectedAlert)}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            background: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        {selectedAlert && (
          <ModalContainer>
            <ModalTitle>Detalhes do alerta</ModalTitle>
            <ModalMessage>
              {selectedAlert.message || 'Este alerta não possui mensagem disponível.'}
            </ModalMessage>
            <ModalMeta>
              {`Dispositivo: ${selectedAlert.device.name}${selectedAlert.sensor?.name ? ` - Sensor: ${selectedAlert.sensor.name}` : ''}`}
            </ModalMeta>
            <ModalTimestamp>
              {`Recebido em ${alertDateFormatter.format(selectedAlert.timestamp)}`}
            </ModalTimestamp>
            <ModalActions>
              <SecondaryButton onClick={handleCloseModal} disabled={isMarkingAsRead}>
                Fechar
              </SecondaryButton>
              <PrimaryButton onClick={handleMarkAsRead} disabled={isMarkingAsRead}>
                Marcar como lida
              </PrimaryButton>
            </ModalActions>
          </ModalContainer>
        )}
      </Dialog>
    </>
  );
};

export default NotificationsPopUp;
